import { useEffect, useMemo, useRef } from 'react';

import { useEvent } from '@hooks/use-event';
import { useScopedTranslate } from '@locale/translate';
import { isJsonEqual } from '@utils/comparison';

import { FilterConfig, getFilterColumnId, getFilterConfigIds } from './filters/common';
import { TableStore } from './table-store';
import { TableView } from './types';

export type ViewSettings<View extends TableView = TableView> = {
  views: Array<View>;
  selectedView: string;
};

export type UseViewsManagerProps<View extends TableView = TableView> = {
  views: Array<View> | undefined;
  selectedViewId: string | undefined;
  tableStore: TableStore;
  filterConfig?: FilterConfig;
  hasExtraUnsavedChanges?: boolean;
  onViewChange?: (view: View) => void;
  getExtraViewData?: () => Partial<View>;
  transformViewBeforeSave?: (view: View, newState: TableView) => View;

  onSaveView: (viewId: string, config: Record<string, any>) => void;
  onCreateView: (view: {
    name: string;
    isCollaborative?: boolean;
    config: Record<string, any>;
  }) => void;
  onUpdateView: (view: View) => void;
  onDeleteView: (viewId: string) => void;
  onSelectView: (viewId: string) => void;
  onReorderViews: (newOrder: Array<string>) => void;
};

export const useViewsManager = <View extends TableView = TableView>({
  views = [],
  selectedViewId,
  tableStore,
  filterConfig,
  hasExtraUnsavedChanges = false,
  onViewChange,
  getExtraViewData,
  transformViewBeforeSave,
  onSaveView,
  onCreateView,
  onUpdateView,
  onDeleteView,
  onSelectView,
  onReorderViews,
}: UseViewsManagerProps<View>) => {
  const { t } = useScopedTranslate(locale);
  const selectedView = useMemo(() => {
    if (!views.length) {
      return undefined;
    }
    return (
      (views.find((view) => view.id === selectedViewId) as View | undefined) ??
      (views[0] as View | undefined)
    );
  }, [views, selectedViewId]);

  // Suppress dirty tracking during view switches to avoid
  // flickering save/discard buttons in transitional renders.
  const isSwitchingViewRef = useRef(false);

  const currentFilters = tableStore.useStore((state) => state.filters);
  const currentSorting = tableStore.useStore((state) => state.sortingState);
  const currentColumnVisibility = tableStore.useStore((state) => state.columnVisibility);
  const availableColumnIds = tableStore.useStore((state) => state._availableColumnIds);

  const activeFilterConfigIds = useMemo(
    () => (filterConfig ? getFilterConfigIds(filterConfig) : undefined),
    [filterConfig]
  );

  const hasUnsavedChanges = useMemo(() => {
    if (!selectedView || isSwitchingViewRef.current) return false;

    const hasChanged = (a: unknown, b: unknown) => !isJsonEqual(a, b);

    const isStaleColumn = (columnId: string) =>
      availableColumnIds.length > 0 && !availableColumnIds.includes(columnId);

    const isActiveFilter = (f: { category: string; field?: string }) =>
      !activeFilterConfigIds || activeFilterConfigIds.has(getFilterColumnId(f));

    const savedVisibility = selectedView.columnVisibility ?? {};
    const isVisibilityDirty = Object.entries(savedVisibility).some(
      ([colId, savedValue]) =>
        !isStaleColumn(colId) && currentColumnVisibility[colId] !== savedValue
    );

    const savedFilters = (selectedView.appliedFilters ?? []).filter(isActiveFilter);
    const currentActiveFilters = currentFilters.appliedFilters.filter(isActiveFilter);
    const isFiltersDirty = hasChanged(currentActiveFilters, savedFilters);

    return (
      hasChanged(currentSorting, selectedView.sortingState ?? []) ||
      isFiltersDirty ||
      currentFilters.conjunction !== (selectedView.conjunction ?? 'and') ||
      isVisibilityDirty ||
      hasExtraUnsavedChanges
    );
  }, [
    currentFilters,
    currentSorting,
    currentColumnVisibility,
    availableColumnIds,
    selectedView,
    activeFilterConfigIds,
    hasExtraUnsavedChanges,
  ]);

  useEffect(() => {
    if (selectedView) {
      tableStore.actions.syncConfig({
        selectedView,
        onPersist,
      });
      onViewChange?.(selectedView);
    }
    isSwitchingViewRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedView?.id]);

  const onPersist = useEvent((newState: TableView) => {
    if (!selectedView) {
      return;
    }

    const viewWithSavedFilters = {
      ...newState,
      appliedFilters: selectedView.appliedFilters ?? [],
      conjunction: selectedView.conjunction ?? 'and',
      sortingState: selectedView.sortingState ?? [],
    };

    const baseView = {
      ...selectedView,
      ...viewWithSavedFilters,
      id: selectedView.id,
    } as View;

    const viewToSave = transformViewBeforeSave
      ? transformViewBeforeSave(baseView, viewWithSavedFilters)
      : baseView;

    const extraData = getExtraViewData?.() ?? {};
    const finalView = { ...viewToSave, ...extraData };

    const { id, name, isCollaborative, isOwn, favoriteItem, ...config } = finalView;

    onSaveView(selectedView.id!, config);
  });

  const saveView = useEvent(() => {
    if (!selectedView?.id) return;

    const fullState = tableStore.actions.getViewState();
    const extraData = getExtraViewData?.() ?? {};
    const finalView = { ...fullState, ...extraData };

    // Strip stale filters (referencing removed/disabled attributes) before saving
    if (finalView.appliedFilters && activeFilterConfigIds) {
      finalView.appliedFilters = finalView.appliedFilters.filter((f) =>
        activeFilterConfigIds.has(getFilterColumnId(f))
      );
    }

    const { id, name, isCollaborative, isOwn, favoriteItem, ...config } = finalView;

    onSaveView(selectedView.id, config);
  });

  const discardChanges = useEvent(() => {
    if (!selectedView) return;
    tableStore.actions.resetFilters(selectedView);
    onViewChange?.(selectedView);
  });

  const createView = useEvent((newView: TableView) => {
    const extraData = getExtraViewData?.() ?? {};
    const viewToCreate = { ...newView, ...extraData };
    const { id, name, isCollaborative, isOwn, favoriteItem, ...config } = viewToCreate;

    onCreateView({
      name: name ?? t('newView'),
      isCollaborative,
      config,
    });
  });

  const updateView = useEvent((updatedView: TableView) => {
    onUpdateView(updatedView as View);
  });

  const deleteView = useEvent((viewId: string) => {
    onDeleteView(viewId);
  });

  const selectView = useEvent((viewId: string) => {
    if (selectedViewId === viewId) {
      return;
    }
    const newSelectedView = views.find((view) => view.id === viewId) as View | undefined;
    if (!newSelectedView) {
      return;
    }

    // Mark as switching so hasUnsavedChanges returns false during
    // transitional renders. Cleared in the useEffect on selectedView?.id.
    isSwitchingViewRef.current = true;

    onSelectView(viewId);
  });

  const reorderViews = useEvent((newOrder: Array<string>) => {
    onReorderViews(newOrder);
  });

  return {
    views,
    selectedView,
    hasUnsavedChanges,
    activeFilterConfigIds,
    onPersist,
    createView,
    updateView,
    deleteView,
    selectView,
    reorderViews,
    saveView,
    discardChanges,
  };
};

const locale = {
  en: {
    newView: 'New view',
  },
  is: {
    newView: 'Ný ásýnd',
  },
};
